import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import {
  Volume2, Play, Pause, SkipForward, SkipBack,
  BookOpen, ChevronRight, ChevronDown, Search
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUDIO_BASE = 'https://www.wordproaudio.net/bibles/app/audio/6';

// Mapping from bible_service book_id to wordproject audio number (1-66)
const BOOK_AUDIO_NUM = {
  gen:1,exo:2,lev:3,num:4,deut:5,jos:6,jue:7,rut:8,'1sa':9,'2sa':10,
  '1re':11,'2re':12,'1cr':13,'2cr':14,esd:15,neh:16,est:17,job:18,sal:19,pro:20,
  ecl:21,cant:22,isa:23,jer:24,lam:25,eze:26,dan:27,ose:28,joel:29,amo:30,
  abd:31,jon:32,miq:33,nah:34,hab:35,sof:36,hag:37,zac:38,mal:39,
  mat:40,mar:41,luc:42,juan:43,hech:44,rom:45,'1cor':46,'2cor':47,gal:48,efe:49,
  fil:50,col:51,'1tes':52,'2tes':53,'1tim':54,'2tim':55,tit:56,file:57,heb:58,sant:59,
  '1ped':60,'2ped':61,'1juan':62,'2juan':63,'3juan':64,judas:65,apoc:66
};

const BibliaAudio = () => {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBook, setSelectedBook] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [showOT, setShowOT] = useState(true);
  const [showNT, setShowNT] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const audioRef = useRef(null);
  const progressInterval = useRef(null);

  useEffect(() => {
    loadBooks();
    return () => { if (progressInterval.current) clearInterval(progressInterval.current); };
  }, []);

  const loadBooks = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/bible/books`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBooks(response.data.books);
    } catch {
      toast.error('Error al cargar libros');
    } finally {
      setLoading(false);
    }
  };

  const getAudioUrl = useCallback((bookId, chapter) => {
    const num = BOOK_AUDIO_NUM[bookId];
    if (!num) return null;
    return `${AUDIO_BASE}/${num}/${chapter}.mp3`;
  }, []);

  const playChapter = useCallback((book, chapter) => {
    const url = getAudioUrl(book.id, chapter);
    if (!url) return;

    setSelectedBook(book);
    setSelectedChapter(chapter);
    setProgress(0);
    setCurrentTime(0);
    setDuration(0);

    if (audioRef.current) {
      audioRef.current.src = url;
      audioRef.current.load();
      audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  }, [getAudioUrl]);

  const togglePlayPause = () => {
    if (!audioRef.current || !selectedBook) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  };

  const nextChapter = useCallback(() => {
    if (!selectedBook) return;
    if (selectedChapter < selectedBook.chapters) {
      playChapter(selectedBook, selectedChapter + 1);
    } else {
      // Go to next book
      const bookIds = Object.keys(BOOK_AUDIO_NUM);
      const currentIdx = bookIds.indexOf(selectedBook.id);
      if (currentIdx < bookIds.length - 1) {
        const nextBookId = bookIds[currentIdx + 1];
        const nextBook = books.find(b => b.id === nextBookId);
        if (nextBook) playChapter(nextBook, 1);
      }
    }
  }, [selectedBook, selectedChapter, books, playChapter]);

  const prevChapter = () => {
    if (!selectedBook) return;
    if (selectedChapter > 1) {
      playChapter(selectedBook, selectedChapter - 1);
    }
  };

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      if (audio.duration) setProgress((audio.currentTime / audio.duration) * 100);
    };
    const onLoadedMetadata = () => setDuration(audio.duration);
    const onEnded = () => { setIsPlaying(false); nextChapter(); };
    const onError = () => toast.error('Error al cargar el audio');

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
    };
  }, [nextChapter]);

  const seekTo = (e) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = x / rect.width;
    audioRef.current.currentTime = pct * duration;
  };

  const formatTime = (s) => {
    if (!s || isNaN(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  // Split books into OT and NT
  const otBookIds = Object.keys(BOOK_AUDIO_NUM).filter(id => BOOK_AUDIO_NUM[id] <= 39);
  const ntBookIds = Object.keys(BOOK_AUDIO_NUM).filter(id => BOOK_AUDIO_NUM[id] > 39);

  const filterBooks = (ids) => {
    const filtered = books.filter(b => ids.includes(b.id));
    if (!searchTerm) return filtered;
    return filtered.filter(b => b.name.toLowerCase().includes(searchTerm.toLowerCase()));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="biblia-audio-page">
      <audio ref={audioRef} preload="metadata" />

      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg">
          <Volume2 className="w-7 h-7 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Biblia en Audio</h1>
          <p className="text-gray-500">Reina Valera 1960 - Escucha la Palabra de Dios</p>
        </div>
      </div>

      {/* Player - Always visible when a book is selected */}
      {selectedBook && (
        <Card className="border-0 shadow-xl bg-gradient-to-r from-amber-600 to-orange-700 text-white overflow-hidden" data-testid="audio-player">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-amber-100 text-sm font-medium">Reproduciendo</p>
                <h3 className="text-xl font-bold">{selectedBook.name} - Capítulo {selectedChapter}</h3>
              </div>
              <Badge className="bg-white/20 text-white border-0">
                {selectedChapter} / {selectedBook.chapters}
              </Badge>
            </div>

            {/* Progress bar */}
            <div
              className="w-full h-2 bg-white/20 rounded-full cursor-pointer mb-2 group"
              onClick={seekTo}
              data-testid="audio-progress-bar"
            >
              <div
                className="h-full bg-white rounded-full transition-all relative"
                style={{ width: `${progress}%` }}
              >
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
            <div className="flex justify-between text-xs text-amber-200 mb-4">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center gap-4">
              <Button
                size="icon"
                variant="ghost"
                className="text-white hover:bg-white/20 h-10 w-10"
                onClick={prevChapter}
                disabled={selectedChapter <= 1}
                data-testid="prev-chapter-btn"
              >
                <SkipBack className="w-5 h-5" />
              </Button>
              <Button
                size="icon"
                className="bg-white text-amber-700 hover:bg-amber-100 h-14 w-14 rounded-full shadow-lg"
                onClick={togglePlayPause}
                data-testid="play-pause-btn"
              >
                {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-0.5" />}
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="text-white hover:bg-white/20 h-10 w-10"
                onClick={nextChapter}
                data-testid="next-chapter-btn"
              >
                <SkipForward className="w-5 h-5" />
              </Button>
            </div>

            {/* Chapter quick nav */}
            <div className="mt-4 flex flex-wrap gap-1 justify-center max-h-28 overflow-y-auto">
              {[...Array(selectedBook.chapters)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => playChapter(selectedBook, i + 1)}
                  className={`w-8 h-8 rounded text-xs font-medium transition-all ${
                    selectedChapter === i + 1
                      ? 'bg-white text-amber-700 shadow'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Buscar libro..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent text-sm"
          data-testid="book-search-input"
        />
      </div>

      {/* Old Testament */}
      <div>
        <button
          onClick={() => setShowOT(!showOT)}
          className="flex items-center gap-2 text-lg font-bold text-gray-700 mb-3 hover:text-amber-700 transition-colors"
          data-testid="toggle-ot"
        >
          {showOT ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          <BookOpen className="w-5 h-5 text-amber-600" />
          Antiguo Testamento
          <Badge variant="outline" className="text-xs ml-2">39 libros</Badge>
        </button>
        {showOT && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {filterBooks(otBookIds).map(book => (
              <BookCard
                key={book.id}
                book={book}
                isActive={selectedBook?.id === book.id}
                onSelect={() => playChapter(book, 1)}
              />
            ))}
          </div>
        )}
      </div>

      {/* New Testament */}
      <div>
        <button
          onClick={() => setShowNT(!showNT)}
          className="flex items-center gap-2 text-lg font-bold text-gray-700 mb-3 hover:text-amber-700 transition-colors"
          data-testid="toggle-nt"
        >
          {showNT ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          <BookOpen className="w-5 h-5 text-orange-600" />
          Nuevo Testamento
          <Badge variant="outline" className="text-xs ml-2">27 libros</Badge>
        </button>
        {showNT && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {filterBooks(ntBookIds).map(book => (
              <BookCard
                key={book.id}
                book={book}
                isActive={selectedBook?.id === book.id}
                onSelect={() => playChapter(book, 1)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const BookCard = ({ book, isActive, onSelect }) => (
  <button
    onClick={onSelect}
    className={`p-3 rounded-xl text-left transition-all border-2 ${
      isActive
        ? 'border-amber-500 bg-amber-50 shadow-md'
        : 'border-gray-100 bg-white hover:border-amber-200 hover:shadow-sm'
    }`}
    data-testid={`book-card-${book.id}`}
  >
    <p className={`font-semibold text-sm truncate ${isActive ? 'text-amber-700' : 'text-gray-800'}`}>
      {book.name}
    </p>
    <p className="text-xs text-gray-400 mt-0.5">{book.chapters} cap.</p>
  </button>
);

export default BibliaAudio;
